import { Dialog, DialogContent } from "@/styles/ui/dialog";
import { forwardRef, useEffect, useState } from "react";
import { ModalFooterFull, ModalHeader } from "@/components/common/modals";
import { cn } from "@/lib/utils";
import { useModalStore } from "@/stores/store";

export const ModalComponent = forwardRef<
  HTMLButtonElement,
  {
    children?: React.ReactNode;
    open?: boolean;
    setOpen?: (open: boolean) => void;
    onConfirm: () => void;
    onCancel: () => void;
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel: string;
    isLoading?: boolean;
    cancelButtonClassName?: string;
    confirmButtonClassName?: string;
    headerClassName?: string;
  }
>(
  (
    {
      children,
      open = false,
      setOpen,
      onConfirm,
      onCancel,
      title,
      description,
      confirmLabel,
      cancelLabel,
      isLoading = false,
      cancelButtonClassName,
      confirmButtonClassName,
      headerClassName,
    },
    ref,
  ) => {
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          onConfirm();
        }
      };
      document.addEventListener("keydown", handleKeyDown, true);
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    }, [onConfirm]);
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="flex flex-col bg-white rounded-md max-w-[280px] p-0 gap-0 border-none shadow-2xl"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ModalHeader
            title={title}
            description={description}
            className={headerClassName}
          />
          {children}
          <ModalFooterFull
            cancelLabel={cancelLabel}
            confirmLabel={confirmLabel}
            onCancel={onCancel}
            onConfirm={onConfirm}
            className="flex gap-2 py-[6px] px-3"
            cancelClassName={cn("grow", cancelButtonClassName)}
            confirmClassName={cn("grow", confirmButtonClassName)}
            isLoading={isLoading}
          />
        </DialogContent>
      </Dialog>
    );
  },
);

export const ModalContainer = () => {
  const { modals, closeModal } = useModalStore();
  const [isProcessing, setIsProcessing] = useState(false);

  if (modals.length === 0) return null;

  const modal = modals[0];

  const handleConfirm = async () => {
    try {
      setIsProcessing(true);

      // onConfirm이 Promise를 반환하는지 확인
      const result = modal.onConfirm?.();
      // Promise인 경우 await
      if (result instanceof Promise) {
        const a = await result;
      }

      // 성공 시 모달 닫기
      closeModal();
    } catch (error) {
      console.error("Modal confirm error:", error);
      // 에러 발생 시 모달은 열린 상태로 유지
      // 필요하면 에러 토스트 표시
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    try {
      setIsProcessing(true);

      // onCancel이 Promise를 반환하는지 확인
      const result = modal.onCancel?.();

      // Promise인 경우 await
      if (result instanceof Promise) {
        await result;
      }

      // 성공 시 모달 닫기
      closeModal();
    } catch (error) {
      console.error("Modal cancel error:", error);
      // 에러 발생 시에도 cancel은 모달을 닫을 수 있음
      closeModal();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ModalComponent
      open={modal.isActive === undefined ? true : modal.isActive}
      setOpen={(open) => {
        if (!open && !isProcessing) {
          closeModal();
        }
      }}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      title={modal.title}
      description={""}
      confirmLabel={modal.confirmLabel}
      cancelLabel={modal.cancelLabel ?? ""}
      confirmButtonClassName={modal.confirmButtonClassName}
      cancelButtonClassName={modal.cancelButtonClassName}
      headerClassName={modal.headerClassName ?? ""}
      isLoading={isProcessing || modal.isLoading}
    >
      {modal.content}
    </ModalComponent>
  );
};
